import type { BlogPost } from '@/data/siteData';

export type BlogSortOrder = 'newest' | 'oldest' | 'category';
type BlogDateSortOrder = Exclude<BlogSortOrder, 'category'>;

export interface BlogPostFilters {
  query?: string;
  category?: string;
  sortOrder?: BlogSortOrder;
}

export const sortBlogPostsByDate = (posts: BlogPost[], order: BlogDateSortOrder = 'newest'): BlogPost[] =>
  [...posts].sort((a, b) => {
    const comparison = a.date.localeCompare(b.date);
    return order === 'newest' ? -comparison : comparison;
  });

export const formatBlogCategory = (category: string): string => {
  const normalized = category.trim().toLocaleLowerCase('id-ID').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  const aliases: Record<string, string> = {
    'ai llm': 'AI & LLM',
    'ai & llm': 'AI & LLM',
    buildinpublic: 'Build in Public',
    'build in public': 'Build in Public',
    devops: 'DevOps',
    cybersecurity: 'Cybersecurity',
  };
  return aliases[normalized] ?? category;
};

export const getBlogCategories = (posts: BlogPost[]): string[] =>
  [...new Set(posts.map(({ tag }) => formatBlogCategory(tag)))]
    .sort((a, b) => a.localeCompare(b, 'id-ID'));

export const filterAndSortBlogPosts = (
  posts: BlogPost[],
  { query = '', category = '', sortOrder = 'newest' }: BlogPostFilters = {},
): BlogPost[] => {
  const normalizedQuery = query.trim().toLocaleLowerCase('id-ID');
  const filtered = posts.filter((post) => {
    const normalizedCategory = formatBlogCategory(post.tag);
    const matchesCategory = !category || normalizedCategory === category;
    const searchableText = `${post.title} ${post.snippet} ${post.tag} ${normalizedCategory} ${post.content}`.toLocaleLowerCase('id-ID');
    return matchesCategory && (!normalizedQuery || searchableText.includes(normalizedQuery));
  });

  if (sortOrder === 'category') {
    return [...filtered].sort((a, b) =>
      formatBlogCategory(a.tag).localeCompare(formatBlogCategory(b.tag), 'id-ID')
      || b.date.localeCompare(a.date)
      || a.title.localeCompare(b.title, 'id-ID'));
  }

  return sortBlogPostsByDate(filtered, sortOrder);
};
